'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { useEffect, useId, useRef, useState } from 'react';
import { BRIEF_ACCEPT, MAX_BRIEF_TEXT, briefFileError, briefFilename, validateBriefAnalysis, type BriefAnalysis } from '@/lib/public/elina-brief';
import ElinaWorkProgress from './ElinaWorkProgress';
import type { ProtectedFetch } from './usePublicProtection';

export default function ElinaBriefUpload({ disabled, verifying = false, onReadingChange, onUse, protectedFetch }: {
  disabled: boolean;
  verifying?: boolean;
  protectedFetch: ProtectedFetch;
  onReadingChange: (reading: boolean) => void;
  onUse: (text: string, filename: string) => Promise<boolean>;
}) {
  const { t, href, content, locale } = useI18n();


  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const editor = useRef<HTMLTextAreaElement>(null);
  const controller = useRef<AbortController | null>(null);
  const [reading, setReading] = useState(false);
  const [using, setUsing] = useState(false);
  const [filename, setFilename] = useState('');
  const [analysis, setAnalysis] = useState<BriefAnalysis | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => { if (analysis && analysis.readability !== 'unreadable') editor.current?.focus({ preventScroll: true }); }, [analysis]);

  const read = async (file: File) => {
    if (disabled || reading || using) return;
    setError('');
    const problem = briefFileError(file);
    if (problem) { setError(t(problem)); return; }
    setAnalysis(null); setText(''); setFilename(briefFilename(file.name)); setReading(true); onReadingChange(true);
    const abort = new AbortController(); controller.current = abort;
    try {
      const form = new FormData(); form.append('file', file);
      const response = await protectedFetch('/api/public/elina/brief', { method: 'POST', body: form, signal: abort.signal });
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.error) throw new Error(data?.error || (response.status === 429 ? t('Plusieurs fichiers ont été analysés récemment. Patientez avant de réessayer.') : '') || t('Le fichier n’a pas pu être analysé. Réessayez avec une version plus légère.'));
      const result = validateBriefAnalysis(data?.analysis);
      setAnalysis(result); setText(result.text);
    } catch (e) {
      if (abort.signal.reason !== 'cancelled') setError(abort.signal.aborted ? t('L’analyse prend trop de temps. Réessayez ou joignez seulement les pages utiles.') : e instanceof Error && e.message !== 'INVALID_BRIEF_ANALYSIS' ? e.message : t('L’analyse n’a pas pu être lue. Réessayez ou décrivez votre besoin dans le message.'));
    } finally { controller.current = null; setReading(false); onReadingChange(false); }
  };
  const useBrief = async () => {
    if (disabled || using || !text.trim()) return;
    setUsing(true);
    try { if (await onUse(text.trim(), filename)) { setAnalysis(null); setText(''); setFilename(''); } }
    finally { setUsing(false); }
  };
  return <section className="elina-brief" aria-label={t("Joindre un brief")}>
    <div className="elina-brief-heading"><button className="elina-secondary-button" type="button" disabled={disabled || reading || using} onClick={() => input.current?.click()}><span aria-hidden="true">＋</span> {analysis ? t('Choisir un autre fichier') : t('Joindre un brief')}</button><span>{t("Word, PDF ou image · 4 Mo maximum")}</span></div>
    <input ref={input} type="file" accept={BRIEF_ACCEPT} hidden aria-label={t("Fichier du brief")} onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void read(file); }} />
    <p className="elina-caption">{t("Vous pouvez transmettre un fichier à Élina dans le cadre de votre demande.")}</p>
    {reading && !verifying && <ElinaWorkProgress kind="brief" request={filename} onCancel={() => controller.current?.abort('cancelled')} />}
    {error && <><p className="elina-error" role="alert">{error}</p><a className="elina-security-contact" href={href("https://calendly.com/gourpil-rdv-partners/services-et-technologies-by-rdv")} target="_blank" rel="noopener noreferrer">{t("Échanger avec l’équipe")}</a></>}
    {analysis && <div className="elina-brief-preview">
      <p className="elina-brief-filename">{filename}</p>
      <h4>{analysis.readability === 'unreadable' ? t('Ce brief n’est pas suffisamment lisible') : t('Voici ce qu’Élina a compris')}</h4>
      {analysis.warnings.length > 0 && <ul className="elina-brief-warnings" aria-label={t("Points à vérifier")}>{analysis.warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>}
      {analysis.readability !== 'unreadable' && <><label htmlFor={id}>{t("Vérifiez ou corrigez cette synthèse du brief.")}</label><textarea ref={editor} id={id} rows={9} maxLength={MAX_BRIEF_TEXT} value={text} onChange={event => setText(event.target.value)} disabled={disabled || using} /><small>{text.length.toLocaleString(locale)}{t('/6 000 caractères')}</small></>}
      <div className="elina-brief-actions">{analysis.readability !== 'unreadable' && <button type="button" className="elina-button" onClick={() => void useBrief()} disabled={disabled || using || !text.trim()}>{using ? verifying ? t('Vérification avant l’envoi…') : t('Élina vous répond…') : t('Utiliser ce brief')}</button>}<button type="button" className="elina-text-link" disabled={disabled || using} onClick={() => { setAnalysis(null); setText(''); setFilename(''); }}>{t("Retirer le fichier")}</button></div>
      <p className="elina-caption">{t("Seule la synthèse que vous validez rejoint l’échange. Le fichier original n’est pas joint au dossier commercial.")}</p>
    </div>}
  </section>;
}


